import { css } from '@emotion/react';
import { Stack, Typography } from '@mui/material';
import { motion } from 'motion/react';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { colors } from '../../../theme/colors';
import { cardStyles } from '../../../theme/component-styles';
import {
  defaultFormatter,
  FMT,
  IPartialFormatterDataModel,
  StandardFormatterId,
} from '../../../util/formatter-service';
import {
  CalculatedField,
  FormulaInputField,
  isCalculatedField,
  useWaterfallFormulas,
} from './useWaterfallFormulas';
import { WaterfallFormulaField } from './WaterfallFormulaField';

export const selectedCellKeyAtom = atom<string | null>(null);
export const highlightedCellsAtom = atom<Set<string> | null>(null);

const FormulasTable = css`
  table-layout: fixed;
  border-collapse: collapse;
  box-shadow: inset 0 0 0 1px ${colors.neutral[20]};
  width: 100%;
`;
const CalcCell = css`
  padding: 0.2rem 0.25rem;
  font-size: 0.75rem;
  border: thin solid ${colors.neutral[20]};
  box-shadow: inset 0 0 0 1px transparent;
  background-color: transparent;
  &.selectable {
    cursor: pointer;
  }
  &.selected {
    box-shadow: inset 0 0 0 2px ${colors.secondary[40]};
    background-color: transparent;
  }
  &.highlighted {
    background-color: ${colors.primary[10]}88;
  }
  transition: all 0.3s;
`;

const ValueCell = css`
  ${CalcCell};
  text-align: right;
`;

const CalcsTitle = css`
  width: 0;
  overflow: visible;
  visibility: visible;
  transition: all;
`;

const CalcsBox = css({
  ...cardStyles,
  padding: 0,
  border: 'none',
});

export function WaterfallFormulasConfig() {
  const { basicParams, customParams, totalProceedsField, perClassProceedsFields, tierFields, allParams } =
    useWaterfallFormulas();

  const selectedKey = useAtomValue(selectedCellKeyAtom);
  const fieldMap = useMemo(() => {
    return [basicParams, customParams, totalProceedsField, perClassProceedsFields, tierFields]
      .flat()
      .reduce((map, field) => {
        return map.set(field.key, field);
      }, new Map<string, FormulaInputField | CalculatedField>());
  }, [basicParams, customParams, totalProceedsField, perClassProceedsFields, tierFields]);
  const selected = fieldMap.get(selectedKey ?? '') as CalculatedField | undefined;

  const tierFieldsByOrder = useMemo(() => {
    return tierFields.reduce((map, field) => {
      if (!map.has(field.tierOrder)) {
        map.set(field.tierOrder, []);
      }
      map.get(field.tierOrder)!.push(field);
      return map;
    }, new Map<number, CalculatedField[]>());
  }, [tierFields]);

  return (
    <Stack mt='1rem'>
      <Typography variant='body2' component={'h4'} noWrap css={CalcsTitle}>
        Waterfall Calculation
      </Typography>
      <motion.div
        animate={{ opacity: [0, 1] }}
        transition={{ duration: 0.5 }}
        key={String(selectedKey)}
        css={CalcsBox}
      >
        <Stack marginBlock='0.5rem' display='grid' alignItems={'center'} width='100%'>
          <WaterfallFormulaField field={selected ?? null} availableVariables={allParams} />
        </Stack>
      </motion.div>

      <Stack
        display='grid'
        gridTemplateColumns={'1fr 1fr'}
        alignItems={'start'}
        columnGap={'1rem'}
        mt='0.4rem'
      >
        <div>
          <Typography variant='caption'>Basic Parameters</Typography>
          <CalcTable fields={basicParams} />
          <br />
          <Typography variant='caption'>Custom Parameters</Typography>
          <CalcTable fields={customParams} />
        </div>
        <Stack display='grid' alignItems={'space-between'}>
          <div>
            <br />
            <CalcTable fields={[totalProceedsField, ...perClassProceedsFields]} />
            <br />
          </div>
          {[...tierFieldsByOrder.entries()].map(([order, fields]) => {
            const tierName = fields[0]?.tierName;
            return (
              <div key={order}>
                <Typography variant='caption'>{tierName ?? `Tier ${order}`}</Typography>
                <CalcTable fields={fields} />
                <br />
              </div>
            );
          })}
        </Stack>
      </Stack>
    </Stack>
  );
}

function CalcTable({ fields }: { fields: (FormulaInputField | CalculatedField)[] }) {
  const [selectedKey, setSelectedKey] = useAtom(selectedCellKeyAtom);
  const [highlighted, setHighlighted] = useAtom(highlightedCellsAtom);

  return (
    <table css={FormulasTable}>
      <tbody>
        {fields.map((field) => {
          const isSelected = selectedKey === field.key;
          const selectable = isCalculatedField(field);
          let classes = '';
          if (selectable) {
            classes = 'selectable';
          }
          if (isSelected) {
            classes += ' selected';
          } else if (highlighted?.has(field.key)) {
            classes += ' highlighted';
          }

          const _onClick = () => {
            setSelectedKey(field.key);
            setHighlighted((field as CalculatedField).inputs ?? new Set());
          };

          const onClick = selectable ? _onClick : undefined;
          let label = field.label;
          const showKey = isCalculatedField(field)
            ? !field.key.includes('-') // show key for top-level formula variables, not tier fields like "lp-1"
            : field.key !== field.label;
          if (showKey) {
            label = `${field.label} (${field.key})`;
          }
          return (
            <tr key={field.key}>
              <td css={CalcCell} className={isCalculatedField(field) ? '' : classes}>
                {label}
              </td>

              <td css={ValueCell} onClick={onClick} className={classes}>
                {FMT.format(
                  (field.formatter as StandardFormatterId | IPartialFormatterDataModel<unknown>) ??
                    defaultFormatter,
                  field.value
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
